import React from "react";
import { Text } from "@foris/avocado-suite";
import { useTranslation } from "react-i18next";
import Modal from "@components/modal";
import {
  InstructorRemovalFlowStatus,
  InstructorRemovalReason,
} from "@context/instructorRemovalFlow/types";
import styles from "./instructorRemovalModal.module.scss";

interface InstructorRemovalModalProps {
  status: InstructorRemovalFlowStatus;
  reasons: InstructorRemovalReason[];
  onGoToBundle: () => void;
  onReturnToAssignment: () => void;
  onAcknowledge: () => void;
  onRetryBeforeSave: () => void;
  onRetryVerification: () => void;
  onCancelBeforeSave: () => void;
  onCloseAfterSave: () => void;
}

const VISIBLE_STATUSES: InstructorRemovalFlowStatus[] = [
  "pre-save-failed",
  "assignment-save-failed",
  "verification-failed",
  "needs-bundle-correction",
  "needs-replacement",
  "warning-only",
];

export const isInstructorRemovalModalVisible = (status: InstructorRemovalFlowStatus) =>
  VISIBLE_STATUSES.includes(status);

const InstructorRemovalModal: React.FC<InstructorRemovalModalProps> = ({
  status,
  reasons,
  onGoToBundle,
  onReturnToAssignment,
  onAcknowledge,
  onRetryBeforeSave,
  onRetryVerification,
  onCancelBeforeSave,
  onCloseAfterSave,
}) => {
  const { t } = useTranslation();
  const prefix = "assignation-edit.instructor-removal";
  const hasResponsibilityReason = reasons.includes("responsibility");
  const hasMissingPrimaryReason = reasons.includes("primary-missing");
  const hasMultiplePrimaryReason = reasons.includes("primary-multiple");

  if (status === "pre-save-failed") {
    return (
      <Modal
        typeState="error"
        title={t(`${prefix}.pre-save-error.title`)}
        show
        className={styles.instructorRemovalModal}
        textButtonPrincipal={t(`${prefix}.actions.retry-save`)}
        onClickPrincipal={onRetryBeforeSave}
        textButtonSecondary={t(`${prefix}.actions.continue-editing`)}
        onClickSecondary={onCancelBeforeSave}
        onClose={onCancelBeforeSave}
      >
        <Text>{t(`${prefix}.pre-save-error.description`)}</Text>
      </Modal>
    );
  }

  if (status === "assignment-save-failed") {
    return (
      <Modal
        typeState="error"
        title={t(`${prefix}.save-error.title`)}
        show
        className={styles.instructorRemovalModal}
        textButtonPrincipal={t(`${prefix}.actions.retry-save`)}
        onClickPrincipal={onRetryBeforeSave}
        textButtonSecondary={t(`${prefix}.actions.continue-editing`)}
        onClickSecondary={onCancelBeforeSave}
        onClose={onCancelBeforeSave}
      >
        <Text>{t(`${prefix}.save-error.description`)}</Text>
      </Modal>
    );
  }

  if (status === "verification-failed") {
    return (
      <Modal
        typeState="error"
        title={t(`${prefix}.verification-error.title`)}
        show
        className={styles.instructorRemovalModal}
        textButtonPrincipal={t(`${prefix}.actions.retry-verification`)}
        onClickPrincipal={onRetryVerification}
        onClose={onCloseAfterSave}
      >
        <Text>{t(`${prefix}.verification-error.description`)}</Text>
      </Modal>
    );
  }

  const isRemovalOutcome = [
    "needs-bundle-correction",
    "needs-replacement",
    "warning-only",
  ].includes(status);

  if (!isRemovalOutcome) return null;

  const isReplacement = status === "needs-replacement";
  const isWarningOnly = status === "warning-only";
  const principalAction = isReplacement
    ? onReturnToAssignment
    : isWarningOnly
      ? onAcknowledge
      : onGoToBundle;
  const principalText = isReplacement
    ? t(`${prefix}.actions.return-to-assignment`)
    : isWarningOnly
      ? t(`${prefix}.actions.acknowledge`)
      : t(`${prefix}.actions.correct-instructors`);

  return (
    <Modal
      typeState="warning"
      title={t(`${prefix}.${isReplacement ? "replacement" : "validation"}.title`)}
      show
      className={styles.instructorRemovalModal}
      textButtonPrincipal={principalText}
      onClickPrincipal={principalAction}
      textButtonSecondary={isWarningOnly ? t(`${prefix}.actions.return-to-assignment`) : undefined}
      onClickSecondary={isWarningOnly ? onReturnToAssignment : undefined}
      onClose={onCloseAfterSave}
    >
      <div className={styles.instructorRemovalModal_messages}>
        {hasResponsibilityReason && <Text>{t(`${prefix}.reasons.responsibility`)}</Text>}
        {hasMissingPrimaryReason && <Text>{t(`${prefix}.reasons.primary-missing`)}</Text>}
        {hasMultiplePrimaryReason && <Text>{t(`${prefix}.reasons.primary-multiple`)}</Text>}
        {isReplacement && <Text>{t(`${prefix}.replacement.description`)}</Text>}
        {isWarningOnly && <Text>{t(`${prefix}.warning-only.description`)}</Text>}
      </div>
    </Modal>
  );
};

export default InstructorRemovalModal;
